import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getMethods, createMethod, updateMethod, deleteMethod } from '../../api/adminApi';
import { PlusCircle, Edit, Trash2, X, Save, ChevronDown, ChevronUp, RefreshCw, Shield } from 'lucide-react';

// Ödeme yöntemi ekleme/düzenleme modal bileşeni
const MethodFormModal = ({ method, onClose, onSubmit, isEditing }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    display_order: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  
  // Düzenleme modunda form verilerini doldur
  useEffect(() => {
    if (isEditing && method) {
      setFormData({
        name: method.name || '',
        code: method.code || '',
        display_order: method.display_order || 0,
        is_active: method.is_active === undefined ? true : method.is_active
      });
    }
  }, [isEditing, method]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Yöntem kodunu formatlama - sadece küçük harf, rakam ve alt çizgi kullanarak
  const formatCode = (value) => {
    return value.toLowerCase().replace(/[^a-z0-9_]/g, '');
  };
  
  const handleCodeChange = (e) => {
    const formattedCode = formatCode(e.target.value);
    setFormData({
      ...formData,
      code: formattedCode
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.code) {
      toast.error('Yöntem adı ve kodu gereklidir');
      return;
    }
    
    setLoading(true);
    
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      toast.error(`İşlem başarısız: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Ödeme Yöntemini Düzenle' : 'Yeni Ödeme Yöntemi Ekle'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Yöntem Adı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yöntem Adı *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {/* Kod */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kod * (sadece küçük harf, rakam ve alt çizgi)
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleCodeChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Bu kod sistem tarafından kullanılacak benzersiz tanımlayıcıdır.
              </p>
            </div>
            
            {/* Sıralama */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Görüntüleme Sırası
              </label>
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {/* Aktif/Pasif */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Yöntem Aktif
              </label>
            </div>
          </div>
          
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-3"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  İşleniyor...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {isEditing ? 'Güncelle' : 'Kaydet'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Yöntem silme onay modalı
const DeleteConfirmModal = ({ method, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  
  const handleDelete = async () => {
    setLoading(true);
    
    try {
      await onConfirm(method.id);
      onClose();
      toast.success(`${method.name} yöntemi silindi`);
    } catch (error) {
      toast.error(`Silme işlemi başarısız: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Ödeme Yöntemini Sil</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6">
          <div className="flex items-center text-red-600 mb-4">
            <Shield size={24} className="mr-2" />
            <span className="font-medium">Dikkat</span>
          </div>
          
          <p className="text-gray-700 mb-4">
            <strong>{method?.name}</strong> ödeme yöntemini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </p>
          
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-3"
            >
              İptal
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  İşleniyor...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Sil
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Ana Ödeme Yöntemi Yönetimi bileşeni
const MethodManager = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modallar
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  
  // Sıralama
  const [sortConfig, setSortConfig] = useState({
    key: 'display_order',
    direction: 'asc'
  });
  
  // Yöntemleri yükle
  useEffect(() => {
    fetchMethods();
  }, []);
  
  // Yöntemleri getir
  const fetchMethods = async () => {
    setRefreshing(true);
    
    try {
      const data = await getMethods();
      setMethods(data);
    } catch (error) {
      toast.error(`Ödeme yöntemleri yüklenirken hata: ${error.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Sıralama ayarla
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    // Yöntemleri sırala
    const sortedMethods = [...methods].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setMethods(sortedMethods);
  };
  
  // Sıralama ikonları
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronDown size={16} className="ml-1 text-gray-400" />;
    }
    
    return sortConfig.direction === 'asc' ? (
      <ChevronUp size={16} className="ml-1 text-primary-500" />
    ) : (
      <ChevronDown size={16} className="ml-1 text-primary-500" />
    );
  };
  
  // Yeni yöntem ekle
  const handleAddMethod = async (methodData) => {
    try {
      const newMethod = await createMethod(methodData);
      setMethods([...methods, newMethod]);
      toast.success('Ödeme yöntemi başarıyla eklendi');
      return newMethod;
    } catch (error) {
      throw error;
    }
  };
  
  // Yöntem güncelle
  const handleUpdateMethod = async (methodData) => {
    try {
      const updatedMethod = await updateMethod(selectedMethod.id, methodData);
      setMethods(methods.map(method => method.id === selectedMethod.id ? updatedMethod : method));
      toast.success('Ödeme yöntemi başarıyla güncellendi');
      return updatedMethod;
    } catch (error) {
      throw error;
    }
  };
  
  // Yöntem sil
  const handleDeleteMethod = async (id) => {
    try {
      await deleteMethod(id);
      setMethods(methods.filter(method => method.id !== id));
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Yükleme durumu
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Ödeme Yöntemi Yönetimi</h2>
        <div className="flex space-x-2">
          <button
            onClick={fetchMethods}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
            disabled={refreshing}
          >
            <RefreshCw size={16} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Yenile
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
          >
            <PlusCircle size={16} className="mr-1" />
            Yeni Yöntem
          </button>
        </div>
      </div>
      
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('display_order')}
                >
                  <div className="flex items-center">
                    Sıra {getSortIcon('display_order')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    Yöntem Adı {getSortIcon('name')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('code')}
                >
                  <div className="flex items-center">
                    Kod {getSortIcon('code')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {methods.length > 0 ? (
                methods.map((method) => (
                  <tr key={method.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{method.display_order}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{method.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">{method.code}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${method.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {method.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          setSelectedMethod(method);
                          setShowEditModal(true);
                        }}
                        className="text-primary-600 hover:text-primary-900 p-1"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMethod(method);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-10 w-10 text-gray-300 mb-3" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <p className="text-lg font-medium">Henüz ödeme yöntemi bulunmuyor</p>
                      <p className="text-sm mt-1 mb-3">
                        Yeni ödeme yöntemleri ekleyerek başlayabilirsiniz
                      </p>
                      <button
                        onClick={() => setShowAddModal(true)}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center"
                      >
                        <PlusCircle size={16} className="mr-1" />
                        Yeni Ödeme Yöntemi Ekle
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modallar */}
      {showAddModal && (
        <MethodFormModal
          method={null}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddMethod}
          isEditing={false}
        />
      )}
      
      {showEditModal && (
        <MethodFormModal
          method={selectedMethod}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateMethod}
          isEditing={true}
        />
      )}
      
      {showDeleteModal && (
        <DeleteConfirmModal
          method={selectedMethod}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteMethod}
        />
      )}
    </div>
  );
};

export default MethodManager;
